// Vista de ejemplo de la Mesa de servicio (CC-CONSOLE#1598).
//
// En CC-CONSOLE la mesa es la CENTRAL de todas las marcas: los tickets de
// ejemplo del Modo Sandbox nunca se mezclan con la mesa real. Verlos es una
// decisión explícita de la persona («Ver tickets de ejemplo»). Mientras la
// vista está activa, cada petición a `/serviceDesk/*` lleva el encabezado
// `x-service-desk-view: sandbox`; la API sólo abre la vista si la compañía
// tiene el Sandbox encendido y responde `sandboxView: { companyId,
// companyName } | null` en la lista y las métricas.
//
// El estado vive en sessionStorage (por pestaña): al cerrar la pestaña se
// vuelve a la mesa real. Toda lectura y escritura del almacenamiento va en
// try/catch: sin él, la vista simplemente arranca apagada.

import { create } from "zustand";

export const SAMPLE_VIEW_HEADER = "x-service-desk-view";
const STORAGE_KEY = "serviceDesk.sampleView";

export interface SandboxView {
  companyId: string;
  companyName: string | null;
}

interface SampleViewState {
  /** ¿La persona pidió ver los tickets de ejemplo? */
  active: boolean;
  /** Lo que confirmó la API en la última respuesta (null: la vista no se abrió). */
  view: SandboxView | null;
  enter: () => void;
  exit: () => void;
  /** Lo llama el cliente de la API con el `sandboxView` de la respuesta. */
  confirm: (view: SandboxView | null) => void;
}

function readActive(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeActive(active: boolean) {
  try {
    if (active) sessionStorage.setItem(STORAGE_KEY, "1");
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* sin almacenamiento: la vista sólo dura esta carga */
  }
}

export const useSampleViewStore = create<SampleViewState>()((set) => ({
  active: readActive(),
  view: null,
  enter: () => {
    writeActive(true);
    set({ active: true });
  },
  exit: () => {
    writeActive(false);
    set({ active: false, view: null });
  },
  confirm: (view) => {
    // La API no abrió la vista (Sandbox apagado, otra compañía, sin permiso):
    // se vuelve a la mesa real para no quedar en un estado a medias.
    if (!view) {
      writeActive(false);
      set({ active: false, view: null });
      return;
    }
    set({ view });
  },
}));

/** Lee `sandboxView` de una respuesta (cuerpo paginado o `data` de métricas). */
export function readSandboxView(raw: unknown): SandboxView | null {
  if (!raw || typeof raw !== "object") return null;
  const v = raw as Record<string, unknown>;
  if (typeof v.companyId !== "string" || !v.companyId) return null;
  return { companyId: v.companyId, companyName: typeof v.companyName === "string" ? v.companyName : null };
}

/** Encabezados de la vista para una petición a `/serviceDesk/*`. */
export function sampleViewHeaders(): Record<string, string> {
  return useSampleViewStore.getState().active ? { [SAMPLE_VIEW_HEADER]: "sandbox" } : {};
}
